import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, panelPost, useAdminGet } from '../kit';
import { ExportButtons, Fact, Pager, Pill, S, TEAM_INBOX, type Tone, num, pageInfo, rowsOf, str, useDebounced } from './shared';

/**
 * Bulk enquiries — the "Contact us for bulk pricing" form on /ip-intel-report
 * (GET ip_intel/bulk_enquiries). Each enquiry was e-mailed to
 * info@universitytech.io with the enquirer in CC and as Reply-To, so the
 * conversation itself happens in the inbox; this tab is the register:
 * who asked, whether someone has answered, and a note for the team.
 * POST ip_intel/bulk_enquiry_update {id, status, admin_note} ·
 * POST ip_intel/bulk_enquiry_resend {id} (re-sends that e-mail).
 */

export const BULK_STATUSES = ['new', 'replied', 'closed'] as const;
const BULK_META: Record<string, { label: string; tone: Tone; hint: string }> = {
  new: { label: 'New', tone: 'warn', hint: 'Nobody has answered yet' },
  replied: { label: 'Replied', tone: 'good', hint: 'The team has answered' },
  closed: { label: 'Closed', tone: 'neutral', hint: 'Done — no further action' },
};

const BulkPill: React.FC<{ status: any }> = ({ status }) => {
  const m = BULK_META[str(status)];
  return <Pill tone={m?.tone || 'neutral'} title={m?.hint}>{m?.label || str(status) || '—'}</Pill>;
};

const MailPill: React.FC<{ status: any; error?: any }> = ({ status, error }) => {
  const s = str(status);
  if (s === 'sent') return <Pill tone="good" title={`Sent to ${TEAM_INBOX}, enquirer in CC`}>Sent</Pill>;
  if (s === 'failed') return <Pill tone="bad" title={str(error) || 'The e-mail did not go out'}>Failed</Pill>;
  // Over the per-address / hourly CC limits: the team got it, the enquirer was not copied.
  if (s === 'sent_team_only') return <Pill tone="warn" title={`Sent to ${TEAM_INBOX} only. ${str(error)}`}>Sent (not copied)</Pill>;
  return <Pill tone="neutral">{s || '—'}</Pill>;
};

const fullName = (r: any) => [str(r.first_name), str(r.last_name)].filter(Boolean).join(' ');

/** Reply from your own mail app: to the enquirer, the team inbox in CC, their message quoted. */
const replyHref = (r: any): string => {
  const quoted = str(r.message).slice(0, 1200).split(/\r?\n/).map(l => `> ${l}`).join('\n');
  const more = str(r.message).length > 1200 ? '\n> …' : '';
  const when = r.created_at ? fmtDate(r.created_at) : '';
  const body = `Hi ${str(r.first_name) || 'there'},\n\n\n\n`
    + `${when ? `On ${when}, ` : ''}${fullName(r) || 'you'} wrote:\n${quoted}${more}\n`;
  const subject = `Re: Bulk pricing enquiry${r.ref ? ` (${r.ref})` : ''}`;
  return `mailto:${encodeURIComponent(str(r.email))}?cc=${encodeURIComponent(TEAM_INBOX)}`
    + `&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const COLS = ['created_at', 'ref', 'name', 'email', 'message', 'status', 'email_status', 'country'];
const LABELS: Record<string, string> = {
  created_at: 'When', ref: 'Ref', name: 'Name', email: 'Email', message: 'Message', status: 'Status',
  email_status: 'E-mail', country: 'Country',
};

type Filters = { q: string; status: string; from: string; to: string };
const EMPTY: Filters = { q: '', status: '', from: '', to: '' };

// ---------------------------------------------------------------------------
const EnquiryDrawer: React.FC<{ row: any; onClose: () => void; onChanged: (row: any | null) => void }> =
  ({ row, onClose, onChanged }) => {
  const [r, setR] = useState<any>(row);
  const [status, setStatus] = useState(str(row.status) || 'new');
  const [note, setNote] = useState(str(row.admin_note));
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [resendArmed, setResendArmed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    if (!resendArmed) return;
    const t = setTimeout(() => setResendArmed(false), 6000);
    return () => clearTimeout(t);
  }, [resendArmed]);

  const apply = (next: any) => {
    if (!next || typeof next !== 'object') return;
    setR((cur: any) => ({ ...cur, ...next }));
    setStatus(str(next.status) || 'new');
    setNote(str(next.admin_note));
  };

  const save = async (body: { status?: string; admin_note?: string }, ok: string) => {
    setBusy('save'); setMsg(null);
    const { data, error } = await panelPost(S, 'bulk_enquiry_update', { id: r.id, ...body });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const next = data?.row ?? data;
    apply(next);
    setMsg({ kind: 'ok', text: ok });
    onChanged(next);
  };

  const resend = async () => {
    if (!resendArmed) { setResendArmed(true); return; }
    setResendArmed(false); setBusy('resend'); setMsg(null);
    const { data, error } = await panelPost(S, 'bulk_enquiry_resend', { id: r.id });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Resend failed: ${error}` }); return; }
    const next = data?.row ?? data;
    apply(next);
    onChanged(next);
    setMsg(data?.sent === false || str(next?.email_status) === 'failed'
      ? { kind: 'error', text: `The e-mail did NOT go out: ${str(next?.email_error) || 'see Email › Logs'}.` }
      : { kind: 'ok', text: `Re-sent to ${TEAM_INBOX} with ${r.email} in CC.` });
  };

  const dirty = status !== (str(r.status) || 'new') || note !== str(r.admin_note);
  const name = fullName(r);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col" style={{ width: 'min(96vw, 44rem)' }}
        onClick={e => e.stopPropagation()} role="dialog" aria-label="Bulk pricing enquiry">
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-grayscale-1300 font-grotesk font-mono">{r.ref || `#${r.id}`}</span>
              <BulkPill status={r.status} />
              <MailPill status={r.email_status} error={r.email_error} />
            </div>
            <div className="text-xs text-grayscale-1100 truncate">{[name, r.email].filter(Boolean).join(' · ')}</div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

          <Section title="Their request"
            right={<span className="text-xs text-grayscale-1100">{r.created_at ? fmtDate(r.created_at) : ''}</span>}>
            <blockquote className="border-l-4 border-primary-400 bg-primary-50 rounded-r-lg px-4 py-3 text-sm
              text-grayscale-1300 whitespace-pre-wrap break-words">
              {str(r.message) || '—'}
            </blockquote>
            <div className="grid grid-cols-2 gap-2">
              <Fact label="Name" value={name} />
              <Fact label="Email" value={r.email ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : ''} />
              <Fact label="Country" value={r.country} />
              <Fact label="Account" value={r.user_id ? <span className="font-mono text-xs">{r.user_id}</span> : 'Not signed in'} />
              <Fact label="Sent from" value={r.page_url
                ? <span className="text-xs break-all">{str(r.page_url).replace(/^https?:\/\/[^/]+/i, '') || '/'}</span> : ''} />
              <Fact label="E-mail to the team" value={
                <span className="flex flex-col gap-0.5">
                  <MailPill status={r.email_status} error={r.email_error} />
                  {str(r.email_status) === 'failed' && r.email_error && (
                    <span className="text-[11px] text-red-600 font-normal">{str(r.email_error)}</span>
                  )}
                </span>} />
            </div>
          </Section>

          <Section title="Reply">
            <div className="flex items-center gap-2 flex-wrap">
              <a href={replyHref(r)}
                className="py-1.5 px-3.5 text-sm rounded-full font-grotesk font-medium whitespace-nowrap bg-primary-500 text-grayscale-100">
                Reply by e-mail
              </a>
              {str(r.status) === 'new' && (
                <Btn small onClick={() => save({ status: 'replied' }, 'Marked replied.')} disabled={!!busy}>
                  Mark replied
                </Btn>
              )}
            </div>
            <div className="text-[11px] text-grayscale-1100">
              Opens your mail app addressed to <b>{r.email}</b> with <b>{TEAM_INBOX}</b> in CC and their message quoted.
              The original e-mail in the {TEAM_INBOX} inbox has them as Reply-To, so “Reply all” there works too.
            </div>
          </Section>

          <Section title="Status & note">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-grayscale-1100">Status</span>
              <Select value={status} onChange={setStatus} options={[...BULK_STATUSES]}
                labels={Object.fromEntries(BULK_STATUSES.map(s => [s, BULK_META[s].label]))} />
              <span className="text-[11px] text-grayscale-1100">{BULK_META[status]?.hint}</span>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="Internal note — never sent to the enquirer (e.g. quoted 10 reports at …)"
              className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            <div className="flex items-center gap-2 flex-wrap">
              <Btn kind="primary" small disabled={!dirty || !!busy}
                onClick={() => save({ status, admin_note: note }, 'Saved.')}>
                {busy === 'save' ? 'Saving…' : 'Save status & note'}
              </Btn>
              {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
              {r.updated_at && (
                <span className="text-[11px] text-grayscale-1100">
                  last change {fmtDate(r.updated_at)}{r.updated_by ? ` by ${r.updated_by}` : ''}
                </span>
              )}
            </div>
          </Section>

          <Section title="Re-send the e-mail">
            <div className="flex items-center gap-2 flex-wrap">
              <Btn small kind={resendArmed ? 'primary' : 'subtle'} onClick={resend} disabled={!!busy}>
                {busy === 'resend' ? 'Sending…' : resendArmed ? `Confirm — e-mail ${TEAM_INBOX} + ${r.email}` : 'Re-send e-mail'}
              </Btn>
              <span className="text-[11px] text-grayscale-1100">
                Sends the enquiry again to {TEAM_INBOX}, with {r.email || 'the enquirer'} in CC — for when the first one
                failed or got lost.
              </span>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const BulkEnquiries: React.FC<{ preset?: { status: string; nonce: number } | null }> = ({ preset }) => {
  const [f, setF] = useState<Filters>({ ...EMPTY, status: preset?.status || '' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const [open, setOpen] = useState<any | null>(null);
  const q = useDebounced(f.q);
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => {
    if (preset) setF({ ...EMPTY, status: preset.status });
  }, [preset?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterParams = useMemo(() => ({ q, status: f.status, from: f.from, to: f.to }), [q, f.status, f.from, f.to]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'bulk_enquiries', { ...filterParams, page, per_page: perPage },
    [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const raw = rowsOf(d, 'enquiries');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, raw, per);
  const counts = d.counts || {};
  const allCount = BULK_STATUSES.reduce((a, s) => a + num(counts[s]), 0);

  const rows = useMemo(() => raw.map(r => ({ ...r, name: fullName(r) })), [raw]);

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.status) chips.push({ label: 'Status', value: BULK_META[f.status]?.label || f.status, clear: () => set('status')('') });
  if (f.from) chips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) chips.push({ label: 'To', value: f.to, clear: () => set('to')('') });

  const kpiStatus = (s: string) => setF(x => ({ ...x, status: x.status === s ? '' : s }));

  return (
    <div className="flex flex-col gap-4">
      {d.counts && (
        <KPIRow>
          {BULK_STATUSES.map(s => (
            <KPI key={s} label={BULK_META[s].label} value={num(counts[s]).toLocaleString()} hint={BULK_META[s].hint}
              onClick={() => kpiStatus(s)} active={f.status === s} />
          ))}
          <KPI label="All enquiries" value={allCount.toLocaleString()} hint="all time"
            onClick={() => set('status')('')} active={!f.status && chips.length === 0} />
        </KPIRow>
      )}
      <Section title="Bulk pricing enquiries"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} enquir{total === 1 ? 'y' : 'ies'}</span>
            {chips.length > 0 && <Btn small onClick={() => setF(EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExportButtons kind="bulk" params={filterParams} onError={setDlErr} what="These enquiries" />
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          From the “Contact us for bulk pricing” form on the page. Each one is e-mailed to {TEAM_INBOX} with the
          enquirer in CC — the e-mail's wording is under the <b>Emails</b> tab.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search name, email, message, ref…" />
          <Select value={f.status} onChange={set('status')} options={['', ...BULK_STATUSES]}
            labels={{ '': 'Any status', ...Object.fromEntries(BULK_STATUSES.map(s => [s,
              `${BULK_META[s].label}${counts[s] !== undefined ? ` (${num(counts[s])})` : ''}`])) }} />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} maxHeight="40rem"
            empty={chips.length ? 'No enquiries match these filters.' : 'No bulk pricing enquiries yet.'}
            render={{
              created_at: r => <span className="text-xs" title={str(r.created_at)}>{fmtDate(r.created_at)}</span>,
              ref: r => (
                <button type="button" onClick={() => setOpen(r)} title="Open this enquiry"
                  className="font-mono text-xs font-bold text-grayscale-1300 underline underline-offset-2">
                  {r.ref || `#${r.id}`}
                </button>
              ),
              name: r => <span className="font-medium">{r.name || '—'}</span>,
              email: r => r.email ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : '—',
              message: r => (
                <span className="block max-w-[22rem] truncate text-xs text-grayscale-1200" title={str(r.message)}>
                  {str(r.message).replace(/\s+/g, ' ') || '—'}
                </span>
              ),
              status: r => <BulkPill status={r.status} />,
              email_status: r => <MailPill status={r.email_status} error={r.email_error} />,
            }}
            actions={r => <Btn small onClick={() => setOpen(r)}>Open</Btn>} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={raw.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>

      {open && (
        <EnquiryDrawer key={str(open.id)} row={open} onClose={() => setOpen(null)}
          onChanged={() => setTick(t => t + 1)} />
      )}
    </div>
  );
};

export default BulkEnquiries;
